import React, { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Production from './pages/Production';
import Sales from './pages/Sales';
import Team from './pages/Team';
import Settings from './pages/Settings';
import Reporting from './pages/Reporting';
import Accounting from './pages/Accounting';
import Compliance from './pages/Compliance';
import { User, Organization, Role } from './types';

// Auth Context Types
interface AuthContextType {
  user: User | null;
  org: Organization | null;
  role: Role | null;
  login: (userData: User, token: string, orgData?: Organization | null, roleData?: Role | null) => void;
  logout: () => void;
  updateOrg: (orgData: Organization, roleData?: Role) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthContext.Provider');
  }
  return context;
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const token = localStorage.getItem('pourix_token');
    const savedUser = localStorage.getItem('pourix_user');
    const savedOrg = localStorage.getItem('pourix_org');
    const savedRole = localStorage.getItem('pourix_role');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        if (savedOrg) setOrg(JSON.parse(savedOrg));
        if (savedRole) setRole(savedRole as Role);
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData: User, token: string, orgData: Organization | null = null, roleData: Role | null = null) => {
    localStorage.setItem('pourix_token', token);
    localStorage.setItem('pourix_user', JSON.stringify(userData));
    if (orgData) localStorage.setItem('pourix_org', JSON.stringify(orgData));
    if (roleData) localStorage.setItem('pourix_role', roleData);
    setUser(userData);
    setOrg(orgData);
    setRole(roleData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pourix_token');
    localStorage.removeItem('pourix_user');
    localStorage.removeItem('pourix_org');
    localStorage.removeItem('pourix_role');
    setUser(null);
    setOrg(null);
    setRole(null);
  }, []);

  const updateOrg = useCallback((orgData: Organization, roleData?: Role) => {
    localStorage.setItem('pourix_org', JSON.stringify(orgData));
    if (roleData) localStorage.setItem('pourix_role', roleData);
    setOrg(orgData);
    if (roleData) setRole(roleData);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0891B2]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, org, role, login, logout, updateOrg }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="production" element={<Production />} />
            <Route path="sales" element={<Sales />} />
            <Route path="team" element={<Team />} />
            <Route path="settings" element={<Settings />} />
            <Route path="reporting" element={<Reporting />} />
            <Route path="accounting" element={<Accounting />} />
            <Route path="compliance" element={<Compliance />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
};

export default App;
